import type { Vendor } from './hooks';

/**
 * Corporate-form words that don't distinguish one vendor from another
 * ("Acme Inc." and "Acme, LLC" are the same supplier). Keep in sync with
 * `public.vendor_name_key` in supabase/migrations/118_vendor_name_dedupe.sql
 * — the DB trigger rejects exact key collisions, this module mirrors it so
 * the form can warn before the round-trip.
 */
const CORPORATE_SUFFIXES = new Set([
  'inc',
  'incorporated',
  'llc',
  'lc',
  'ltd',
  'limited',
  'co',
  'corp',
  'corporation',
  'company',
  'plc',
  'lp',
  'llp',
  'pllc',
  'gmbh',
]);

/**
 * Canonical comparison key for a vendor name: lowercase, "&" → "and",
 * punctuation and whitespace dropped, a leading "The" and trailing
 * corporate suffixes removed. "The Acme Co." and "ACME" share a key.
 */
export function vendorNameKey(name: string): string {
  const tokens = name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\./g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  if (tokens.length > 1 && tokens[0] === 'the') tokens.shift();
  while (tokens.length > 1 && CORPORATE_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();
  return tokens.join('');
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = curr;
  }
  return prev[b.length];
}

export interface VendorNameMatches {
  /** Vendor whose key is identical — creating another would be a duplicate. */
  exact: Vendor | null;
  /** Close-but-not-identical names (typos, extra words) worth a second look. */
  similar: Vendor[];
}

/**
 * Cross-reference a candidate vendor name against the existing directory.
 * `excludeId` skips the vendor being edited.
 */
export function findVendorNameMatches(
  name: string,
  vendors: Vendor[],
  excludeId?: string | null,
): VendorNameMatches {
  const key = vendorNameKey(name);
  if (!key) return { exact: null, similar: [] };

  let exact: Vendor | null = null;
  const scored: { vendor: Vendor; score: number }[] = [];
  for (const v of vendors) {
    if (v.id === excludeId) continue;
    const other = vendorNameKey(v.name);
    if (!other) continue;
    if (other === key) {
      if (!exact || (!exact.is_active && v.is_active)) exact = v;
      continue;
    }
    if (Math.min(key.length, other.length) < 4) continue;
    const longest = Math.max(key.length, other.length);
    const tolerance = longest <= 5 ? 1 : longest <= 10 ? 2 : 3;
    const distance = levenshtein(key, other);
    if (distance <= tolerance) {
      scored.push({ vendor: v, score: distance });
    } else if (key.includes(other) || other.includes(key)) {
      scored.push({ vendor: v, score: tolerance + 1 });
    }
  }

  scored.sort((a, b) => a.score - b.score || a.vendor.name.localeCompare(b.vendor.name));
  return { exact, similar: scored.slice(0, 5).map((s) => s.vendor) };
}
